// src/app/work-order-numbers/services/work-order-upload.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

import { WorkOrderImportRow } from '../models/work-order-import-row.model';

export interface CurrentUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  roles: { id: number; name: string }[];
  jobTitleRoles: any[];
  departmentRoles: any[];
  jobTitle: string | null;
  reportTo: string | null;
}

export interface WorkOrderDto {
  won: string;
  project: string;
  departmentCode: string;
  description: string | null;
  isActive: boolean;
  isOverhead: boolean;
}

export interface WorkOrderNumber {
  id: number;
  won: string;
  project: string;
  departmentCode: string;
  description: string | null;
  isActive: boolean;
  isOverhead: boolean;
}

export interface ApiResponse<T> {
  status: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class WorkOrderUploadService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private readonly http: HttpClient) {}

  uploadRows(rows: WorkOrderImportRow[]): Observable<WorkOrderNumber[]> {
    const payload: WorkOrderDto[] = rows.map((r) => this.mapRowToApi(r));
    return this.http
      .post<ApiResponse<WorkOrderNumber[]>>(
        `${this.baseUrl}/api/work-order-numbers/multiple`,
        payload
      )
      .pipe(map((res) => res.data));
  }

  getCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${this.baseUrl}/api/account/get-current-user`);
  }

  private mapRowToApi(row: WorkOrderImportRow): WorkOrderDto {
    return {
      won: this.toTrimmedString(row.WON),
      project: this.toTrimmedString(row.Project),
      departmentCode: this.toTrimmedString(row.DepartmentCode),
      description: this.toOptionalTrimmedString(row.Description),
      isActive: this.parseBoolean(row.IsActive),
      isOverhead: this.parseBoolean(row.IsOverhead),
    };
  }

  private toTrimmedString(value: unknown): string {
    return (value ?? '').toString().trim();
  }

  private toOptionalTrimmedString(value: unknown): string | null {
    const s = (value ?? '').toString().trim();
    return s.length ? s : null;
  }

  private parseBoolean(value: WorkOrderImportRow['IsActive'] | WorkOrderImportRow['IsOverhead']): boolean {
    if (value === null || value === undefined) return false;

    if (typeof value === 'boolean') return value;

    if (typeof value === 'number') return value !== 0;

    const s = String(value).trim().toLowerCase();
    if (['true', 't', 'yes', 'y', '1'].includes(s)) return true;
    if (['false', 'f', 'no', 'n', '0'].includes(s)) return false;

    return false;
  }
}